import { useQuery } from '@tanstack/react-query'
import type { Session, User } from '@supabase/supabase-js'
import { ACTIVE_SUBSCRIPTION_STATUSES } from '@quoter/shared/schemas'
import { profileQueryOptions, supabaseSessionQueryOptions, type Profile } from './queries'

/**
 * Every state the app can be in, as one discriminated union. Components branch
 * on `status` and never have to reason about "authenticated but not yet
 * provisioned" as a combination of booleans.
 *
 *   unprovisioned    — signed in, but no membership in this product (checkout
 *                      never completed, the webhook has not landed yet, or the
 *                      account belongs to another product)
 *   no_subscription  — provisioned, but the subscription lapsed
 */
export type SessionState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'unprovisioned'; user: User; session: Session }
  | {
      status: 'no_subscription'
      user: User
      session: Session
      profile: Profile
      /** Raw Stripe status, so the paywall can tell "update your card" from "buy again". */
      subscriptionStatus: string | null
      isAdmin: boolean
    }
  | {
      status: 'authenticated'
      user: User
      session: Session
      profile: Profile
      organizationId: string
      isAdmin: boolean
      isPlatformAdmin: boolean
    }

function decodeJwtClaims(token: string): Record<string, unknown> {
  const parts = token.split('.')
  if (parts.length < 2) return {}
  try {
    return JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >
  } catch {
    return {}
  }
}

export function useSession(): SessionState {
  const sessionQuery = useQuery(supabaseSessionQueryOptions)
  const session = sessionQuery.data ?? null
  const userId = session?.user.id ?? null

  const profileQuery = useQuery(profileQueryOptions(userId))
  const profileData = profileQuery.data ?? null

  const isLoading = sessionQuery.isLoading || (!!userId && profileQuery.isLoading)

  if (isLoading) return { status: 'loading' }
  if (!session) return { status: 'unauthenticated' }

  if (!profileData) return { status: 'unprovisioned', user: session.user, session }

  const { profile, subscriptionStatus } = profileData
  const isPlatformAdmin = decodeJwtClaims(session.access_token).is_platform_admin === true

  // Platform operators bypass the paywall — they need to reach a tenant to
  // support it, and their access is not billed.
  if (!isPlatformAdmin && !ACTIVE_SUBSCRIPTION_STATUSES.has(subscriptionStatus ?? '')) {
    return {
      status: 'no_subscription',
      user: session.user,
      session,
      profile,
      subscriptionStatus,
      isAdmin: profile.role === 'admin',
    }
  }

  return {
    status: 'authenticated',
    user: session.user,
    session,
    profile,
    organizationId: profile.organization_id,
    isAdmin: profile.role === 'admin',
    isPlatformAdmin,
  }
}
